import React, { useEffect, useRef, useState } from "react";

import PhysarumRenderer, {
  DEFAULT_PHYSARUM_PARAMETERS,
  MAX_STRAINS,
} from "./PhysarumRenderer";

import "./PhysarumDemo.css";

const INTERACTION_PRESETS = {
  independent: {
    label: "Independent",
    matrix: [
      [1, 0, 0, 0],
      [0, 1, 0, 0],
      [0, 0, 1, 0],
      [0, 0, 0, 1],
    ],
  },

  attraction: {
    label: "Mutual Attraction",
    matrix: [
      [1, 1, 1, 1],
      [1, 1, 1, 1],
      [1, 1, 1, 1],
      [1, 1, 1, 1],
    ],
  },

  repulsion: {
    label: "Mutual Repulsion",
    matrix: [
      [1, -1, -1, -1],
      [-1, 1, -1, -1],
      [-1, -1, 1, -1],
      [-1, -1, -1, 1],
    ],
  },

  predatorPrey: {
    label: "Predator / Prey",
    matrix: [
      [1, 1, 0, 0],
      [-1, 1, 0, 0],
      [0, 0, 1, 0],
      [0, 0, 0, 1],
    ],
  },

  complex: {
    label: "Complex",
    matrix: [
      [1.0, 0.8, -0.7, 0.4],
      [-0.6, 1.0, 0.9, -0.8],
      [0.7, -0.9, 1.0, 0.6],
      [-0.8, 0.7, -0.6, 1.0],
    ],
  },
};

function cloneParameters(parameters) {
  return {
    ...parameters,
    strains: parameters.strains.map((strain) => ({ ...strain })),
    interactions: parameters.interactions.map((row) => [...row]),
  };
}

function InteractionSlider({ target, value, color, onChange }) {
  return (
    <div className="interaction-row">
      <div className="interaction-label">
        <span className="strain-color-dot" style={{ background: color }} />
        <span>{`Trail ${target + 1}`}</span>
      </div>

      <input
        type="range"
        min={-1}
        max={1}
        step={0.1}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />

      <strong>{value > 0 ? `+${value.toFixed(1)}` : value.toFixed(1)}</strong>
    </div>
  );
}

function Slider({ label, value, min, max, step, displayValue, onChange }) {
  return (
    <div className="reconstruction-control">
      <div className="reconstruction-control-header">
        <span>{label}</span>
        <strong>{displayValue ?? value}</strong>
      </div>

      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </div>
  );
}

function ColorControl({ value, onChange }) {
  return (
    <div className="reconstruction-control color-control">
      <div className="reconstruction-control-header">
        <span>Trail Color</span>
        <strong>{value.toUpperCase()}</strong>
      </div>

      <input
        type="color"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label="Trail color"
      />
    </div>
  );
}

const PHYSARUM_INFO = {
  eyebrow: "GPU SIMULATION · PHYSARUM",
  title: "Physarum Transport Networks",

  description:
    "A real-time agent simulation inspired by the decentralized foraging behavior of Physarum slime molds. Millions of simple agents sense nearby trail fields, steer in response to local signals, deposit new trail material, and collectively form branching transport networks without centralized control.",

  implementation: [
    {
      text: "Agent simulation runs in parallel using WebGPU compute shaders",
      links: [
        {
          term: "WebGPU",
          url: "https://webgpu.org/",
        },
      ],
    },
    "Agent state is maintained in a GPU storage buffer and updated every frame",
    "Trail fields are stored in ping-pong GPU textures and updated by a separate compute pass",
    "Agent deposits are accumulated into a GPU storage buffer before diffusion and decay",
    "Supports multiple strains with independently configurable sensing, movement, diffusion, decay, and deposition",
    "A strain interaction matrix controls attraction and repulsion between different trail fields",
    "Interactive boundaries and attraction / repulsion forces modify agent behavior at runtime",
  ],

  pipeline: [
    "Sense Trails",
    "Steer & Move",
    "Deposit",
    "Diffuse & Decay",
    "Render Trails",
  ],

  references: [
    {
      title:
        "Characteristics of Pattern Formation and Evolution in Approximations of Physarum Transport Networks",
      meta: "Jeff Jones · Artificial Life, 2010",
      url: "https://doi.org/10.1162/artl.2010.16.2.16202",
    },
  ],
};

export default function PhysarumDemo({ Panel, onInfoChange }) {
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  const pointerDownRef = useRef(false);

  const [status, setStatus] = useState("Initializing...");

  const [toast, setToast] = useState(null);
  const [parameters, setParameters] = useState(() =>
    cloneParameters(DEFAULT_PHYSARUM_PARAMETERS),
  );
  const [selectedStrain, setSelectedStrain] = useState(0);
  const [tool, setTool] = useState("wall");
  const [brushSize, setBrushSize] = useState(14);
  const [forceStrength, setForceStrength] = useState(20);

  const [cursor, setCursor] = useState({
    x: 0,
    y: 0,
    radius: 0,
    visible: false,
  });

  useEffect(() => {
    onInfoChange?.(PHYSARUM_INFO);
  }, [onInfoChange]);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return undefined;
    }

    let cancelled = false;

    async function initialize() {
      try {
        const renderer = new PhysarumRenderer(canvas);

        renderer.setParameters(parameters);
        await renderer.initialize();

        if (cancelled) {
          renderer.destroy();
          return;
        }

        rendererRef.current = renderer;
        renderer.start();
        setStatus("Ready");
      } catch (error) {
        console.error("[Physarum] Initialization failed:", error);

        showWebGPUError(error);
      }
    }

    initialize();

    return () => {
      cancelled = true;
      rendererRef.current?.destroy();
      rendererRef.current = null;
    };

    // Renderer initialization intentionally uses only the initial parameters.
    // Runtime parameter changes are forwarded through rendererRef.
  }, []);

  function updateStrainParameter(name, value) {
    setParameters((current) => {
      const strains = current.strains.map((strain, index) => {
        if (index !== selectedStrain) {
          return strain;
        }

        return {
          ...strain,
          [name]: value,
        };
      });

      rendererRef.current?.setStrainParameter(selectedStrain, name, value);

      return {
        ...current,
        strains,
      };
    });
  }

  function applyRandomInteractions() {
    setParameters((current) => {
      const interactions = current.interactions.map((row, source) =>
        row.map((_, target) => {
          // Each strain always follows its own trail.
          if (source === target) {
            return 1;
          }

          return Math.round((Math.random() * 2 - 1) * 10) / 10;
        }),
      );

      rendererRef.current?.setParameters({
        interactions,
      });

      return {
        ...current,
        interactions,
      };
    });
  }

  function addStrain() {
    if (parameters.strainCount >= MAX_STRAINS) {
      return;
    }

    const nextCount = parameters.strainCount + 1;

    const next = {
      ...parameters,
      strainCount: nextCount,
    };

    setParameters(next);
    setSelectedStrain(nextCount - 1);

    rendererRef.current?.setParameters(next);
    rendererRef.current?.reset();
  }

  function removeStrain() {
    if (parameters.strainCount <= 1) {
      return;
    }

    const nextCount = parameters.strainCount - 1;

    const next = {
      ...parameters,
      strainCount: nextCount,
    };

    setParameters(next);

    setSelectedStrain((current) => Math.min(current, nextCount - 1));

    rendererRef.current?.setParameters(next);
    rendererRef.current?.reset();
  }

  function updateInteraction(target, value) {
    setParameters((current) => {
      const interactions = current.interactions.map((row) => [...row]);

      interactions[selectedStrain][target] = value;

      rendererRef.current?.setInteraction(selectedStrain, target, value);

      return {
        ...current,
        interactions,
      };
    });
  }

  function applyInteractionPreset(presetName) {
    const preset = INTERACTION_PRESETS[presetName];

    if (!preset) {
      return;
    }

    const interactions = preset.matrix.map((row) => [...row]);

    setParameters((current) => ({
      ...current,
      interactions,
    }));

    rendererRef.current?.setParameters({
      interactions,
    });
  }

  function updateAgentCount(value) {
    const agentCount = Math.round(value);

    setParameters((current) => ({
      ...current,
      agentCount,
    }));

    rendererRef.current?.setParameters({
      agentCount,
    });

    rendererRef.current?.reset();
  }

  function updateWrapBounds(enabled) {
    setParameters((current) => ({
      ...current,
      wrapBounds: enabled,
    }));

    rendererRef.current?.setParameters({
      wrapBounds: enabled,
    });
  }

  function resetSimulation() {
    rendererRef.current?.reset();
  }

  function resetParameters() {
    const defaults = cloneParameters(DEFAULT_PHYSARUM_PARAMETERS);

    setParameters(defaults);
    setSelectedStrain(0);

    rendererRef.current?.setParameters(defaults);
    rendererRef.current?.reset();
  }

  const strain = parameters.strains[selectedStrain];

  function getPointerPosition(event) {
    const canvas = canvasRef.current;

    if (!canvas) {
      return null;
    }

    const rect = canvas.getBoundingClientRect();

    return {
      rect,
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  function updateCursor(event) {
    const pointer = getPointerPosition(event);

    if (!pointer) {
      return;
    }

    setCursor({
      x: pointer.x,
      y: pointer.y,
      radius: (brushSize * pointer.rect.width) / 1024,
      visible: true,
    });
  }

  function hideCursor() {
    setCursor((current) => ({
      ...current,
      visible: false,
    }));

    pointerDownRef.current = false;
    rendererRef.current?.clearPointerInteraction();
  }

  function interactAtPointer(event) {
    const pointer = getPointerPosition(event);
    const renderer = rendererRef.current;

    if (!pointer || !renderer) {
      return;
    }

    const normalizedX = Math.max(
      0,
      Math.min(1, pointer.x / pointer.rect.width),
    );

    const normalizedY = Math.max(
      0,
      Math.min(1, pointer.y / pointer.rect.height),
    );

    if (tool === "wall" || tool === "erase") {
      renderer.paintBoundary(
        normalizedX,
        normalizedY,
        brushSize,
        tool === "wall" ? 1 : 0,
      );

      return;
    }

    if (tool === "attract" || tool === "repel") {
      renderer.setPointerInteraction(
        normalizedX,
        normalizedY,
        brushSize,
        tool === "attract" ? forceStrength : -forceStrength,
      );
    }
  }

  function handlePointerDown(event) {
    pointerDownRef.current = true;

    event.currentTarget.setPointerCapture(event.pointerId);

    updateCursor(event);
    interactAtPointer(event);
  }

  function handlePointerMove(event) {
    updateCursor(event);

    if (!pointerDownRef.current) {
      return;
    }

    interactAtPointer(event);
  }

  function handlePointerUp(event) {
    pointerDownRef.current = false;
    rendererRef.current?.clearPointerInteraction();

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  function clearWalls() {
    rendererRef.current?.clearBoundaries();
  }

  function showWebGPUError(error) {
    const message =
      error instanceof Error ? error.message : "WebGPU initialization failed";

    setStatus(message);

    setToast({
      title: "WebGPU unavailable",
      message:
        "This demo requires WebGPU and a compatible browser/GPU configuration.",
    });
  }

  return (
    <div className="reconstruction-workspace">
      <aside className="reconstruction-sidebar">
        <Panel title="SIMULATION">
          <div className="reconstruction-stat">
            <span>Status</span>
            <strong>{status}</strong>
          </div>

          <Slider
            label="Agents"
            value={parameters.agentCount}
            min={10_000}
            max={10_000_000}
            step={10_000}
            displayValue={parameters.agentCount.toLocaleString()}
            onChange={updateAgentCount}
          />

          <div className="reconstruction-stat">
            <span>Strains</span>
            <strong>{parameters.strainCount}</strong>
          </div>

          <div className="reconstruction-stat">
            <span>Wrap Bounds</span>

            <button
              type="button"
              className={
                parameters.wrapBounds
                  ? "reconstruction-button active"
                  : "reconstruction-button"
              }
              onClick={() => updateWrapBounds(!parameters.wrapBounds)}
            >
              {parameters.wrapBounds ? "ON" : "OFF"}
            </button>
          </div>
        </Panel>

        <Panel title="STRAINS">
          <div className="strain-selector">
            {Array.from({
              length: parameters.strainCount,
            }).map((_, index) => (
              <button
                key={index}
                type="button"
                className={
                  selectedStrain === index
                    ? "strain-button active"
                    : "strain-button"
                }
                onClick={() => setSelectedStrain(index)}
              >
                <span
                  className="strain-color-dot"
                  style={{
                    background: parameters.strains[index].color,
                  }}
                />
                {index + 1}
              </button>
            ))}
          </div>

          <div className="strain-count-controls">
            <button
              type="button"
              className="reconstruction-button"
              onClick={removeStrain}
              disabled={parameters.strainCount <= 1}
            >
              − STRAIN
            </button>

            <button
              type="button"
              className="reconstruction-button"
              onClick={addStrain}
              disabled={parameters.strainCount >= MAX_STRAINS}
            >
              + STRAIN
            </button>
          </div>
        </Panel>

        <Panel title={`STRAIN ${selectedStrain + 1}`}>
          <ColorControl
            value={strain.color}
            onChange={(value) => updateStrainParameter("color", value)}
          />

          <Slider
            label="Speed"
            value={strain.speed}
            min={0.01}
            max={0.4}
            step={0.01}
            displayValue={strain.speed.toFixed(2)}
            onChange={(value) => updateStrainParameter("speed", value)}
          />

          <Slider
            label="Sensor Distance"
            value={strain.sensorDistance}
            min={1}
            max={50}
            step={1}
            displayValue={`${strain.sensorDistance}px`}
            onChange={(value) => updateStrainParameter("sensorDistance", value)}
          />

          <Slider
            label="Sensor Angle"
            value={strain.sensorAngle}
            min={0}
            max={90}
            step={1}
            displayValue={`${strain.sensorAngle}°`}
            onChange={(value) => updateStrainParameter("sensorAngle", value)}
          />

          <Slider
            label="Turn Speed"
            value={strain.turnSpeed}
            min={0}
            max={10}
            step={0.1}
            displayValue={strain.turnSpeed.toFixed(1)}
            onChange={(value) => updateStrainParameter("turnSpeed", value)}
          />

          <Slider
            label="Diffusion"
            value={strain.diffusion}
            min={0}
            max={1}
            step={0.01}
            displayValue={strain.diffusion.toFixed(2)}
            onChange={(value) => updateStrainParameter("diffusion", value)}
          />

          <Slider
            label="Decay"
            value={strain.decay}
            min={0.9}
            max={1}
            step={0.001}
            displayValue={strain.decay.toFixed(3)}
            onChange={(value) => updateStrainParameter("decay", value)}
          />

          <Slider
            label="Deposit"
            value={strain.depositStrength}
            min={0.001}
            max={0.25}
            step={0.001}
            displayValue={strain.depositStrength.toFixed(3)}
            onChange={(value) =>
              updateStrainParameter("depositStrength", value)
            }
          />
        </Panel>

        <Panel title="INTERACTIONS">
          <div className="interaction-description">
            {`STRAIN ${selectedStrain + 1} RESPONSE`}
          </div>

          {Array.from({
            length: parameters.strainCount,
          }).map((_, target) => (
            <InteractionSlider
              key={target}
              target={target}
              value={parameters.interactions[selectedStrain][target]}
              color={parameters.strains[target].color}
              onChange={(value) => updateInteraction(target, value)}
            />
          ))}
        </Panel>

        <Panel title="PRESETS">
          <div className="control-section">
            <div className="interaction-presets">
              {Object.entries(INTERACTION_PRESETS).map(([key, preset]) => (
                <button
                  key={key}
                  type="button"
                  className="interaction-preset-button"
                  onClick={() => applyInteractionPreset(key)}
                >
                  {preset.label}
                </button>
              ))}

              <button
                type="button"
                className="interaction-preset-button"
                onClick={applyRandomInteractions}
              >
                Random
              </button>
            </div>
          </div>
        </Panel>

        <Panel title="">
          <button
            type="button"
            className="reconstruction-button"
            onClick={resetSimulation}
          >
            RESET SIMULATION
          </button>

          <button
            type="button"
            className="reconstruction-button"
            onClick={resetParameters}
          >
            RESET DEFAULTS
          </button>
        </Panel>
      </aside>

      <main className="reconstruction-viewer">
        {toast && (
          <div className="graphics-toast graphics-toast-error" role="alert">
            <div className="graphics-toast-content">
              <strong>{toast.title}</strong>
              <span>{toast.message}</span>
            </div>

            <button
              type="button"
              className="graphics-toast-close"
              onClick={() => setToast(null)}
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        )}
        <canvas
          ref={canvasRef}
          className="physarum-canvas"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerEnter={updateCursor}
          onPointerLeave={hideCursor}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onContextMenu={(event) => event.preventDefault()}
        />

        {cursor.visible && (
          <div
            className={`physarum-brush-preview ${tool}`}
            style={{
              left: cursor.x,
              top: cursor.y,
              width: cursor.radius * 2,
              height: cursor.radius * 2,
            }}
          />
        )}

        <div className="physarum-tool-bar">
          <span className="physarum-tool-label">TOOLS</span>

          <div className="boundary-tool-selector">
            <button
              type="button"
              className={
                tool === "wall"
                  ? "reconstruction-button active"
                  : "reconstruction-button"
              }
              onClick={() => setTool("wall")}
            >
              WALL
            </button>

            <button
              type="button"
              className={
                tool === "erase"
                  ? "reconstruction-button active"
                  : "reconstruction-button"
              }
              onClick={() => setTool("erase")}
            >
              ERASE
            </button>

            <button
              type="button"
              className={
                tool === "attract"
                  ? "reconstruction-button active"
                  : "reconstruction-button"
              }
              onClick={() => setTool("attract")}
            >
              ATTRACT
            </button>

            <button
              type="button"
              className={
                tool === "repel"
                  ? "reconstruction-button active"
                  : "reconstruction-button"
              }
              onClick={() => setTool("repel")}
            >
              REPEL
            </button>
          </div>

          <div className="physarum-brush-control">
            <span>Brush</span>

            <input
              type="range"
              min={2}
              max={64}
              step={1}
              value={brushSize}
              onChange={(event) => setBrushSize(Number(event.target.value))}
            />

            <strong>{brushSize}px</strong>
          </div>

          <div className="physarum-brush-control">
            <span>Strength</span>

            <input
              type="range"
              min={1}
              max={50}
              step={1}
              value={forceStrength}
              onChange={(event) => setForceStrength(Number(event.target.value))}
            />

            <strong>{forceStrength}</strong>
          </div>

          <button
            type="button"
            className="reconstruction-button"
            onClick={clearWalls}
          >
            CLEAR
          </button>
        </div>
      </main>
    </div>
  );
}
